import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LimitCheck, usePlan } from '../context/PlanContext';
import './UsageGuard.css';

// ── Inline limit banner (shown inside a page section) ─────────

interface BannerProps {
  check:   LimitCheck;
  label:   string;   // e.g. "machines"
  compact?: boolean;
}

export const LimitBanner: React.FC<BannerProps> = ({ check, label, compact }) => {
  const navigate = useNavigate();
  const { nextPlan } = usePlan();
  const next = nextPlan();

  if (!check.isWarning && !check.isBlocked) return null;

  if (compact) {
    return (
      <span className={`limit-chip ${check.isBlocked ? 'chip-blocked' : 'chip-warning'}`}>
        {check.isBlocked ? '🚫' : '⚠️'} {check.pct}% used
      </span>
    );
  }

  return (
    <div className={`limit-banner ${check.isBlocked ? 'banner-blocked' : 'banner-warning'}`}>
      <div className="lb-left">
        <span className="lb-icon">{check.isBlocked ? '🚫' : '⚠️'}</span>
        <div>
          <div className="lb-title">
            {check.isBlocked ? `${label} limit reached` : `Approaching ${label} limit`}
          </div>
          <div className="lb-reason">{check.reason}</div>
        </div>
      </div>
      {next && (
        <button className="lb-upgrade-btn" onClick={() => navigate('/app/billing')}>
          Upgrade to {next.name} →
        </button>
      )}
    </div>
  );
};

// ── Usage progress bar ─────────────────────────────────────────

interface ProgressProps {
  label:  string;
  used:   number;
  limit:  number;
  unit?:  string;
  icon?:  string;
}

export const UsageBar: React.FC<ProgressProps> = ({ label, used, limit, unit = '', icon = '' }) => {
  const { usagePct } = usePlan();
  const pct   = limit === -1 ? 0 : usagePct(used, limit);
  const color = pct >= 100 ? '#f87171' : pct >= 80 ? '#fbbf24' : '#34d399';

  return (
    <div className="usage-bar-row">
      <div className="ubr-header">
        <span className="ubr-label">{icon} {label}</span>
        <span className="ubr-value" style={{ color }}>
          {used}{unit} / {limit === -1 ? '∞' : `${limit}${unit}`}
        </span>
      </div>
      <div className="ubr-track">
        <div
          className="ubr-fill"
          style={{ width: `${limit === -1 ? 0 : pct}%`, background: color }}
        />
      </div>
      {pct >= 80 && limit !== -1 && (
        <div className="ubr-hint" style={{ color }}>
          {pct >= 100 ? 'Limit reached — upgrade to continue' : `${pct}% used`}
        </div>
      )}
    </div>
  );
};

// ── Guard wrapper — blocks or warns before executing an action ─

interface GuardProps {
  check:    LimitCheck;
  onAction: () => void;
  children: React.ReactElement;
}

export const UsageGuard: React.FC<GuardProps> = ({ check, onAction, children }) => {
  const navigate = useNavigate();
  const { nextPlan } = usePlan();
  const [showBlock, setShowBlock] = useState(false);

  const handleClick = () => {
    if (check.isBlocked) { setShowBlock(true); return; }
    onAction();
  };

  return (
    <>
      <span onClick={handleClick} style={{ display: 'contents' }}>
        {children}
      </span>

      {showBlock && (
        <div className="modal-overlay" onClick={() => setShowBlock(false)}>
          <div className="upgrade-modal card" onClick={e => e.stopPropagation()}>
            <div className="um-icon">🚫</div>
            <h3>Plan Limit Reached</h3>
            <p>{check.reason}</p>
            {nextPlan() && (
              <div className="um-next-plan">
                <div className="um-plan-name" style={{ color: nextPlan()!.color }}>
                  {nextPlan()!.name} — {nextPlan()!.priceLabel}
                </div>
                <ul className="um-features">
                  {nextPlan()!.features.slice(0, 4).map((f, i) => (
                    <li key={i}>✅ {f}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="um-actions">
              <button className="btn-secondary" onClick={() => setShowBlock(false)}>Cancel</button>
              <button className="btn-primary" onClick={() => { setShowBlock(false); navigate('/app/billing'); }}>
                View Upgrade Options →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsageGuard;
